import { useCallback, useEffect, useState } from 'react';
import { apiErrorMessage } from '../api/client';
import * as formulationsApi from '../api/formulationsApi';
import { Card, EmptyState, ErrorBanner, Skeleton } from './ui';
import { FormulationIO } from './FormulationDetail';
import { IconChevronDown } from './icons';
import { AdminFormulationFull } from '../types/formulation';
import { formatDateTime } from '../utils/format';

/**
 * Everything one customer has generated, on their detail page.
 *
 * Collapsed by default — a regular is a dozen of these and the page is about
 * the person, not their formula history. Opening one shows the answers they
 * gave and the blend those answers produced.
 *
 * One card per GENERATION. The engine also computes an alternative formula
 * beside each primary, but only the primary is ever revealed to the customer,
 * so listing both would show a perfume they were never offered.
 */
export function UserFormulations({ userId }: { userId: string }) {
  const [rows, setRows] = useState<AdminFormulationFull[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setRows(await formulationsApi.getUserFormulations(userId));
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  const title = `Formulations${rows ? ` (${rows.length})` : ''}`;

  if (loading) {
    return (
      <Card title="Formulations">
        <div className="space-y-2">
          <Skeleton className="h-12" />
          <Skeleton className="h-12" />
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card title="Formulations">
        <ErrorBanner message={error} onRetry={load} />
      </Card>
    );
  }

  return (
    <Card title={title}>
      {!rows || rows.length === 0 ? (
        <EmptyState
          message="No formulations yet"
          hint="This customer hasn't finished the quiz"
        />
      ) : (
        <div className="space-y-2">
          {rows.map((f) => {
            const open = openId === f.id;
            return (
              <div key={f.id} className="overflow-hidden rounded-xl border border-edge">
                <button
                  type="button"
                  aria-expanded={open}
                  onClick={() => setOpenId(open ? null : f.id)}
                  className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition-colors hover:bg-edge/20"
                >
                  <span className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                    <span className="text-sm font-medium text-gold">{f.cluster_label}</span>
                    <span className="text-xs text-sub">·</span>
                    <span className="text-xs text-sub">{formatDateTime(f.created_at)}</span>
                  </span>
                  <IconChevronDown
                    className={`h-4 w-4 shrink-0 text-sub transition-transform ${open ? 'rotate-180' : ''}`}
                  />
                </button>
                {open && (
                  <div className="border-t border-edge p-4">
                    <FormulationIO
                      request={f.request}
                      response={f.response}
                      clusterLabel={f.cluster_label}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
